/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
// Load locale-specific relative date/time formatting rules.
import { withTranslation } from 'react-i18next';
import { Alert, Button, Modal, notification, Select, Space } from 'antd';
import { withConfigContext } from '../../../../../../../components/ConfigContext';
import { Link } from 'react-router-dom';
import { isAuthorized } from '../../../../../../../services/utils/authorizationHandler';
import axios from 'axios';
import { handleApiError } from '../../../../../../../services/utils/errorHandler';
import AddRole from '../../../../Roles/components/RolesTable/components/AddRole';
import HtmlComments from '../../../../../../../components/ReactHtmlComments/HtmlComments';
import DeviceOperationsDrawer from '../DeviceOperationsDrawer';
import GroupMapping from '../GroupMapping';
import GroupForm from '../GroupForm';

class TopGroups extends Component {
  constructor(props) {
    super(props);
    this.config = this.props.context;
    this.state = {
      deviceGroups: [],
      selectedGroup: null,
      children: [],
      editModalVisible: false,
      shareModalVisible: false,
      groupDataObject: {},
      rolesData: [],
      shareRolesData: [],
      loading: false,
      popoverVisible: false,
      group: {},
      groupId: null,
      selectedGroupId: null,
      isOperationsVisible: false,
      parentGroupIds: '',
      tab: null,
    };
  }

  componentDidMount() {
    const { tab, children } = this.props;
    this.getGroupsList();
    this.setState({ tab, children });
  }

  componentDidUpdate(prevProps) {
    if (this.props.children !== prevProps.children) {
      this.setState({
        children: [...this.props.children],
      });
    }
  }

  onConfirmDeleteGroup = groupId => {
    const config = this.props.context;
    const { t } = this.props;
    // send request to the invoker
    axios
      .delete(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          '/groups/id/' +
          groupId,
        { headers: { 'Content-Type': 'application/json' } },
      )
      .then(res => {
        if (res.status === 200) {
          this.getGroupsList();
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_deleteGroupMsg'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_deleteGroupError'), t);
      });
  };

  getGroupsList = () => {
    let apiUrl;
    const { t } = this.props;
    if (isAuthorized(this.config.scopes, ['perm:admin-groups:view'])) {
      apiUrl =
        window.location.origin +
        this.config.serverConfig.invoker.uri +
        this.config.serverConfig.invoker.deviceMgt +
        '/admin/groups/hierarchy';
    } else if (isAuthorized(this.config.scopes, ['perm:groups:groups'])) {
      apiUrl =
        window.location.origin +
        this.config.serverConfig.invoker.uri +
        this.config.serverConfig.invoker.deviceMgt +
        '/groups/hierarchy';
    } else {
      notification.error({
        message: t('api_errorMsg'),
        duration: 0,
        description: t('noPerm_viewGroupsDescription'),
      });
    }

    // in the backend, there is a validation limiting the value to 100, which is why it is set to 100 here.
    apiUrl += '?offset=0&limit=100';

    // send request to the invokers
    axios
      .get(apiUrl)
      .then(res => {
        if (res.status === 200) {
          this.setState({
            deviceGroups: res.data.data.deviceGroups,
          });
        }
      })
      .catch(error => {
        handleApiError(
          error,
          t('api_loadError', { label: t('label_deviceGroups') }),
          t,
        );
      });
  };

  fetchUserRoles = (params = {}) => {
    const config = this.props.context;
    const { t } = this.props;
    const apiUrl =
      window.location.origin +
      config.serverConfig.invoker.uri +
      config.serverConfig.invoker.deviceMgt +
      '/roles';

    // send request to the invokerss
    axios
      .get(apiUrl)
      .then(res => {
        if (res.status === 200) {
          this.setState({
            rolesData: res.data.data.roles,
          });
        }
      })
      .catch(error => {
        handleApiError(
          error,
          t('api_loadError', { label: t('home_menu_roles') }),
          t,
        );
      });
  };

  onConfirmShareGroup = data => {
    const config = this.props.context;
    const { t } = this.props;
    // send request to the invoker
    axios
      .post(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          '/groups/id/' +
          this.state.groupId +
          '/share',
        data,
      )
      .then(res => {
        if (res.status === 200) {
          this.getGroupsList();
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_groupShareMsg'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_shareGroupError'), t);
      });
  };

  fetchSharedRoles = id => {
    const config = this.props.context;
    const { t } = this.props;
    const apiUrl =
      window.location.origin +
      config.serverConfig.invoker.uri +
      config.serverConfig.invoker.deviceMgt +
      '/groups/id/' +
      id +
      '/roles';

    axios
      .get(apiUrl)
      .then(res => {
        if (res.status === 200) {
          this.setState({
            shareRolesData: res.data.data.roles,
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_loadShareRolesError'), t);
      });
  };

  openShareModal = groupId => {
    this.setState({
      shareModalVisible: true,
      groupId: groupId,
    });
    if (isAuthorized(this.config.scopes, ['perm:roles:view'])) {
      this.fetchUserRoles();
    }
    if (isAuthorized(this.config.scopes, ['perm:groups:roles'])) {
      this.fetchSharedRoles(groupId);
    }
  };

  handleShareOk = e => {
    this.setState({
      shareModalVisible: false,
    });
    this.onConfirmShareGroup(this.state.shareRolesData);
  };

  handleRolesDropdownChange = value => {
    this.setState({
      shareRolesData: value,
    });
  };

  handleShareCancel = e => {
    this.setState({
      shareModalVisible: false,
    });
  };

  onAddOperations = groupId => {
    this.setState({
      isOperationsVisible: true,
      groupId: groupId,
    });
  };

  onOperationsClose = () => {
    this.setState({
      isOperationsVisible: false,
    });
  };

  openEditModal = group => {
    this.setState({
      editModalVisible: true,
      group: group,
    });
  };
  handleEditCancel = e => {
    this.setState({
      editModalVisible: false,
    });
  };

  OpenExplore = groupId => {
    this.setState({ groupId: groupId });
  };

  fetchNewGroupData = values => {
    this.onConfirmUpdateGroup(values);
  };

  onConfirmUpdateGroup = values => {
    const config = this.props.context;
    const { t } = this.props;
    const data = {
      name: values.name.trim(),
      description: values.description,
      id: this.state.group.id,
      owner: this.state.group.owner,
      groupProperties: this.state.group.groupProperties,
    };
    // send request to the invoker
    axios
      .put(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          '/groups/id/' +
          this.state.group.id,
        data,
      )
      .then(res => {
        if (res.status === 200) {
          this.getGroupsList();
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_updateGroupMsg'),
          });
          this.setState({ editModalVisible: false });
        }
      })
      .catch(error => {
        if (error.response.status === 409) {
          handleApiError(error, error.response.data.data.replace('.', ''), t);
        } else {
          handleApiError(
            error,
            t('api_updateError', { label: t('home_menu_group') }),
            t,
          );
        }
      });
  };
  render() {
    const { t, filters } = this.props;
    let item = this.state.rolesData.map(data => (
      <Select.Option value={data} key={data}>
        {data}
      </Select.Option>
    ));
    const { name, description } = this.state.group;
    const { deviceGroups, tab, children } = this.state;
    const values = {
      name,
      description,
    };
    return (
      <div>
        <GroupMapping
          deviceGroups={deviceGroups}
          OpenExplore={this.OpenExplore}
          openShareModal={this.openShareModal}
          openEditModal={this.openEditModal}
          onAddOperations={this.onAddOperations}
          onConfirmDeleteGroup={this.onConfirmDeleteGroup}
          /* eslint-disable-next-line react/no-children-prop */
          children={children}
          tab={tab}
          filters={filters}
        />
        <div>
          <Modal
            title={t('label_shareGroup')}
            width="500px"
            open={this.state.shareModalVisible}
            onOk={() => this.handleShareOk(this.state.groupId)}
            onCancel={this.handleShareCancel}
            maskClosable={false}
            footer={[
              <Space key="footer-buttons">
                <Link
                  key="add-new-role"
                  to={`/${this.config.appName}/groups?add-new-role=true`}
                >
                  <Button key="new-role" onClick={this.onClickNewRole}>
                    {t('label_newRole')}
                  </Button>
                </Link>
                <Button
                  key="submit"
                  type="primary"
                  onClick={this.handleShareOk}
                >
                  {t('label_share')}
                </Button>
              </Space>,
            ]}
          >
            <div style={{ background: '#f0f2f5' }}>
              <AddRole fetchRoles={this.fetchUserRoles} />
            </div>
            {(!isAuthorized(this.config.scopes, [
              'perm:roles:create-combined-role',
            ]) ||
              !isAuthorized(this.config.scopes, [
                'perm:roles:view',
                'perm:groups:roles',
              ])) && (
              <div>
                <HtmlComments
                  permission={
                    '/permission/admin/device-mgt/roles/manage OR\n' +
                    '/permission/admin/device-mgt/roles/view\n' +
                    '/permission/admin/device-mgt/groups/roles/view'
                  }
                />
                <Alert
                  message={t('noPerm_someFields')}
                  banner
                  style={{ marginBottom: 15 }}
                />
              </div>
            )}
            <p>{t('form_searchUserRoles')}</p>
            <Select
              mode="multiple"
              value={this.state.shareRolesData}
              style={{ width: '100%' }}
              onChange={this.handleRolesDropdownChange}
            >
              {item}
            </Select>
          </Modal>
          <div>
            <Modal
              title={t('label_updateGroup')}
              open={this.state.editModalVisible}
              onCancel={this.handleEditCancel}
              footer={null}
              maskClosable={false}
            >
              <p>{t('newGroupDescription_infoTxt')}</p>
              <GroupForm
                action={'Update'}
                modalVisibility={this.state.editModalVisible}
                values={values}
                callback={values => this.fetchNewGroupData(values)}
              />
            </Modal>
          </div>
        </div>
        {this.state.isOperationsVisible && (
          <DeviceOperationsDrawer
            groupIDList={[this.state.groupId]}
            onOperationsClose={this.onOperationsClose}
          />
        )}
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(TopGroups));
